import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const ShiftForm = ({ selectedShift, onSave, onCancel, onPreview }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    date: '',
    startTime: '',
    endTime: '',
    requiredSkills: [],
    payGrade: '',
    maxEmployees: 1,
    specialInstructions: '',
    isRecurring: false,
    recurringPattern: 'weekly',
    recurringEndDate: '',
    templateName: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Mock data
  const departments = [
    { value: 'customer-service', label: 'Kundenservice' },
    { value: 'operations', label: 'Betrieb' },
    { value: 'manufacturing', label: 'Fertigung' },
    { value: 'administration', label: 'Verwaltung' },
    { value: 'security', label: 'Sicherheit' }
  ];

  const skills = [
    { value: 'customer-support', label: 'Kundensupport' },
    { value: 'technical-skills', label: 'Technische Fähigkeiten' },
    { value: 'leadership', label: 'Führung' },
    { value: 'data-entry', label: 'Dateneingabe' },
    { value: 'quality-control', label: 'Qualitätskontrolle' },
    { value: 'safety-certified', label: 'Sicherheitszertifiziert' }
  ];

  const payGrades = [
    { value: 'entry', label: 'Einstieg (€12/Std)' },
    { value: 'standard', label: 'Standard (€15/Std)' },
    { value: 'senior', label: 'Senior (€18/Std)' },
    { value: 'lead', label: 'Leitung (€22/Std)' },
    { value: 'supervisor', label: 'Supervisor (€25/Std)' }
  ];

  const recurringPatterns = [
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'biweekly', label: 'Zweiwöchentlich' },
    { value: 'monthly', label: 'Monatlich' }
  ];

  useEffect(() => {
    if (selectedShift) {
      setFormData({
        title: selectedShift?.title || '',
        department: selectedShift?.department || '',
        date: selectedShift?.date || '',
        startTime: selectedShift?.startTime || '',
        endTime: selectedShift?.endTime || '',
        requiredSkills: selectedShift?.requiredSkills || [],
        payGrade: selectedShift?.payGrade || '',
        maxEmployees: selectedShift?.maxEmployees || 1,
        specialInstructions: selectedShift?.specialInstructions || '',
        isRecurring: selectedShift?.isRecurring || false,
        recurringPattern: selectedShift?.recurringPattern || 'weekly',
        recurringEndDate: selectedShift?.recurringEndDate || '',
        templateName: selectedShift?.templateName || ''
      });
    }
  }, [selectedShift]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Trigger preview update
    if (onPreview) {
      onPreview({
        ...formData,
        [field]: value
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Schichttitel ist erforderlich';
    }

    if (!formData?.department) {
      newErrors.department = 'Abteilung ist erforderlich';
    }

    if (!formData?.date) {
      newErrors.date = 'Datum ist erforderlich';
    }

    if (!formData?.startTime) {
      newErrors.startTime = 'Startzeit ist erforderlich';
    }

    if (!formData?.endTime) {
      newErrors.endTime = 'Endzeit ist erforderlich';
    }

    if (formData?.startTime && formData?.endTime && formData?.startTime >= formData?.endTime) {
      newErrors.endTime = 'Endzeit muss nach der Startzeit liegen';
    }

    if (!formData?.payGrade) {
      newErrors.payGrade = 'Lohnstufe ist erforderlich';
    }

    if (formData?.maxEmployees < 1) {
      newErrors.maxEmployees = 'Mindestens 1 Mitarbeiter erforderlich';
    }

    if (formData?.isRecurring && !formData?.recurringEndDate) {
      newErrors.recurringEndDate = 'Enddatum für wiederkehrende Schicht erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onSave) {
        onSave(formData);
      }
    } catch (error) {
      console.error('Error saving shift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAsTemplate = () => {
    if (!formData?.templateName?.trim()) {
      setErrors(prev => ({
        ...prev,
        templateName: 'Template-Name ist erforderlich'
      }));
      return;
    }

    // Save as template logic would go here
    console.log('Saving as template:', formData?.templateName);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {selectedShift ? 'Schicht bearbeiten' : 'Neue Schicht erstellen'}
        </h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={onCancel}
            iconName="X"
            iconPosition="left"
            iconSize={16}
          >
            Abbrechen
          </Button>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Input
            label="Schichttitel"
            type="text"
            placeholder="z.B. Morgenschicht Kundenservice"
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            error={errors?.title}
            required
          />

          <Select
            label="Abteilung"
            placeholder="Abteilung auswählen"
            options={departments}
            value={formData?.department}
            onChange={(value) => handleInputChange('department', value)}
            error={errors?.department}
            required
          />
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Input
            label="Datum"
            type="date"
            value={formData?.date}
            onChange={(e) => handleInputChange('date', e?.target?.value)}
            error={errors?.date}
            required
          />

          <Input
            label="Startzeit"
            type="time"
            value={formData?.startTime}
            onChange={(e) => handleInputChange('startTime', e?.target?.value)}
            error={errors?.startTime}
            required
          />

          <Input
            label="Endzeit"
            type="time"
            value={formData?.endTime}
            onChange={(e) => handleInputChange('endTime', e?.target?.value)}
            error={errors?.endTime}
            required
          />
        </div>

        {/* Skills and Pay */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Select
            label="Erforderliche Fähigkeiten"
            placeholder="Fähigkeiten auswählen"
            options={skills}
            value={formData?.requiredSkills}
            onChange={(value) => handleInputChange('requiredSkills', value)}
            multiple
            searchable
          />

          <Select
            label="Lohnstufe"
            placeholder="Lohnstufe auswählen"
            options={payGrades}
            value={formData?.payGrade}
            onChange={(value) => handleInputChange('payGrade', value)}
            error={errors?.payGrade}
            required
          />
        </div>

        {/* Max Employees */}
        <Input
          label="Maximale Anzahl Mitarbeiter"
          type="number"
          min="1"
          max="50"
          value={formData?.maxEmployees}
          onChange={(e) => handleInputChange('maxEmployees', parseInt(e?.target?.value) || 1)}
          error={errors?.maxEmployees}
          required
        />

        {/* Special Instructions */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Besondere Anweisungen
          </label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            rows="4"
            placeholder="Zusätzliche Informationen oder Anweisungen für diese Schicht..."
            value={formData?.specialInstructions}
            onChange={(e) => handleInputChange('specialInstructions', e?.target?.value)}
          />
        </div>

        {/* Recurring Options */}
        <div className="border-t border-border pt-6">
          <Checkbox
            label="Wiederkehrende Schicht"
            description="Diese Schicht regelmäßig erstellen"
            checked={formData?.isRecurring}
            onChange={(e) => handleInputChange('isRecurring', e?.target?.checked)}
          />

          {formData?.isRecurring && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Select
                label="Wiederholungsmuster"
                options={recurringPatterns}
                value={formData?.recurringPattern}
                onChange={(value) => handleInputChange('recurringPattern', value)}
              />

              <Input
                label="Enddatum"
                type="date"
                value={formData?.recurringEndDate}
                onChange={(e) => handleInputChange('recurringEndDate', e?.target?.value)}
                error={errors?.recurringEndDate}
                required
              />
            </div>
          )}
        </div>

        {/* Template Options */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center space-x-4">
            <Input
              label="Als Vorlage speichern"
              type="text"
              placeholder="Vorlagenname eingeben"
              value={formData?.templateName}
              onChange={(e) => handleInputChange('templateName', e?.target?.value)}
              error={errors?.templateName}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveAsTemplate}
              iconName="Save"
              iconPosition="left"
              iconSize={16}
              className="mt-6"
            >
              Vorlage speichern
            </Button>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Abbrechen
          </Button>
          
          <Button
            type="submit"
            loading={isLoading}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
          >
            {selectedShift ? 'Schicht aktualisieren' : 'Schicht erstellen'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ShiftForm;